import React, { useState, useEffect, useMemo } from 'react';
import DataTable from '../components/common/DataTable';
import { DataForm } from '../components/common/DataForm';
import SearchBar from '../components/common/SearchBar';
import PageHeader from '../components/common/PageHeader';
import Pagination from '../components/common/Pagination';
import DeleteConfirmModal from '../components/common/DeleteConfirmModal';
import useDebouncedSearch from '../hooks/useDebouncedSearch';
import { Hash, Info, FileText } from 'lucide-react';
import toast from 'react-hot-toast';

const MOCK_DATA = [
  { id: 1, section: 'Mission Statement', updated: '2024-01-01', description: 'To promote tennis cricket nationwide.' },
  { id: 2, section: 'Vision Statement', updated: '2024-01-02', description: 'To represent India in international tournaments.' }
];

const COLUMNS = [
  { header: 'Section Title', accessor: 'section' },
  { header: 'Last Modified', accessor: 'updated' }
];

const FORM_FIELDS = [
  { label: 'Illustration/Banner', accessor: 'image', type: 'file', fullWidth: true },
  { label: 'Section Heading', accessor: 'section', placeholder: 'e.g. Mission, Vision, History' },
  { label: 'Modified Date', accessor: 'updated', type: 'date' },
  { label: 'Section Text', accessor: 'description', type: 'editor', placeholder: 'Content for about page', fullWidth: true }
];

export default function About() {
  const [dataList, setDataList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingData, setEditingData] = useState(null);
  
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // Search & Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const { searchQuery, setSearchQuery, debouncedQuery } = useDebouncedSearch(() => setCurrentPage(1));
  const itemsPerPage = 5;

  const fetchData = async () => {
    setIsLoading(true);
    // Simulate network delay
    setTimeout(() => {
      setDataList(MOCK_DATA);
      setIsLoading(false);
    }, 500);
  };

  useEffect(() => {
    fetchData();
  }, []);



  const filteredData = useMemo(() => {
    let result = dataList;
    if (debouncedQuery) {
      result = result.filter(item => 
        Object.values(item).some(val => 
          String(val).toLowerCase().includes(debouncedQuery.toLowerCase())
        )
      );
    }
    return result;
  }, [dataList, debouncedQuery]);

  const kpis = useMemo(() => {
    const total = dataList.length;
    return [
      { label: "Content Sections", value: total, icon: <Info size={20} />, color: 'blue' },
      { label: "Updated Sections", value: dataList.filter(d => d.updated).length, icon: <FileText size={20} />, color: 'orange' },
      { label: "Illustrations", value: dataList.filter(d => d.image).length, icon: <Hash size={20} />, color: 'green' }
    ];
  }, [dataList]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleFormSubmit = async (formData) => {
    if (editingData) {
      setDataList(prev => prev.map(item => item.id === editingData.id ? { ...item, ...formData } : item));
      toast.success('About section content updated successfully!');
    } else {
      const newItem = { ...formData, id: Date.now() };
      setDataList(prev => [newItem, ...prev]);
      toast.success('About section content added successfully!');
    }
    setIsFormOpen(false);
    setEditingData(null);
  };

  const handleEdit = (row) => {
    setEditingData(row);
    setIsFormOpen(true);
  };

  const handleDeleteClick = (row) => {
    setDeleteTarget(row);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteExecute = async () => {
    if (!deleteTarget) return;
    setIsDeleteModalOpen(false);
    setDataList(prev => prev.filter(item => item.id !== deleteTarget.id));
    toast.success('Content section deleted successfully!');
    setDeleteTarget(null);
  };

  return (
    <div className="page-content">
      <PageHeader 
        title="About Page Content"
        subtitle="Manage primary informational sections and illustrative banners for the About page"
        onAdd={() => { setEditingData(null); setIsFormOpen(true); }}
        addLabel="Add Section"
        kpis={kpis}
      />
      
      {isFormOpen && (
        <DataForm 
          title={editingData ? `Edit About Content` : `Add New About Content`} 
          fields={FORM_FIELDS} 
          initialData={editingData}
          onSubmit={handleFormSubmit}
          onClose={() => setIsFormOpen(false)}
        />
      )}
      
      <div className="table-card-container">
        <div className="table-actions-area">
          <div className="table-actions-left">
            <SearchBar 
              placeholder="Search about contents..."
              value={searchQuery}
              onChange={setSearchQuery}
            />
          </div>
        </div>

        <div className="table-responsive-wrapper">
          <DataTable 
            columns={COLUMNS} 
            data={paginatedData} 
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
            loading={isLoading}
          />
        </div>

        <Pagination 
          currentPage={currentPage}
          totalPages={totalPages}
          totalEntries={filteredData.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setDeleteTarget(null); }}
        onConfirm={handleDeleteExecute}
        title="Delete Content Section"
        message="Are you sure you want to delete this About page content section?"
        itemName={deleteTarget ? deleteTarget.section : ''}
      />
    </div>
  );
}
